"use client";

import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { FormField, FormItem, FormControl, FormMessage, FormLabel } from "@/components/ui/form";
import { StepProps, BaseFormData } from "../components/types";
import { ValidationRules } from "../components/FormValidation";
import parse from "html-react-parser";
import Link from "next/link";
import { useFormContext, useWatch } from "react-hook-form";
import { cn } from "@/lib/utils";

export interface ContactDetailsStepConfig {
  showTitle?: boolean;
  showFirstName?: boolean;
  showLastName?: boolean;
  showWorkEmail?: boolean;
  showPhone?: boolean;
  showHowDidYouHear?: boolean;
  showReferredBy?: boolean;
  showMessage?: boolean;
  showTermsAccepted?: boolean;
  showMarketingConsent?: boolean;
  showCreditReportConsent?: boolean;
  messageHelperText?: string;
}

type ContactDetailsStepProps<T = BaseFormData> = StepProps<T>;

export const ContactDetailsStep = <T extends BaseFormData = BaseFormData>({ title, description, formOptions, stepConfig }: ContactDetailsStepProps<T>) => {
  const { control, formState } = useFormContext();

  // Extract stepConfig with type safety
  const config = stepConfig as ContactDetailsStepConfig | undefined;

  // Default configuration - show all fields by default
  const fieldConfig = config || {
    showTitle: true,
    showFirstName: true,
    showLastName: true,
    showWorkEmail: true,
    showPhone: true,
    showHowDidYouHear: true,
    showReferredBy: true,
    showMessage: true,
    showTermsAccepted: true,
    showMarketingConsent: true,
    showCreditReportConsent: false,
  };
  
  // Watch the howDidYouHear field to conditionally show referredBy field
  const howDidYouHear = useWatch({ control, name: "howDidYouHear" });
  
  // Check if referredBy field should be shown
  const shouldShowReferredBy = (
    howDidYouHear === "Business referral (e.g. accountant, client, partner)" ||
    howDidYouHear === "Financial adviser or broker"
  );

  const titles = formOptions.titles;
  const referralSources = formOptions.referralSources;

  return (
    <div className="flex flex-col gap-12">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <h3>
          {title}
        </h3>
        {description && (
          <p className="paragraph-lg text-muted-foreground [&_strong]:text-foreground">
            {parse(description)}
          </p>
        )}
      </div>

      {/* Form Fields */}
      <div className="flex flex-col gap-6">

        {(fieldConfig.showTitle || fieldConfig.showFirstName || fieldConfig.showLastName) && (
          <div className={cn(
            "grid gap-6 md:gap-4",
            fieldConfig.showTitle ? "md:grid-cols-11" : "md:grid-cols-8",
          )}>

            {fieldConfig.showTitle && (
              <div className="flex flex-col gap-2 md:col-span-3">
                <FormLabel className="paragraph-base font-bold !text-foreground">Title</FormLabel>
                <FormField
                  control={control}
                  name={"title" as never}
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <Select
                          value={(field.value as string) || ""}
                          onValueChange={(value) => { field.onChange(value); }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            {titles.map((t: string) => (
                              <SelectItem key={t} value={t}>{t}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            )}

            {fieldConfig.showFirstName && (
              <div className="flex flex-col gap-2 md:col-span-4">
                <FormField
                  control={control}
                  name={"firstName" as never}
                  rules={{
                    ...ValidationRules.required("First name"),
                    ...ValidationRules.minLength("First name", 2)
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={(field.value as string) || ""}
                          onChange={(e) => { field.onChange(e); }}
                          maxLength={500}
                          placeholder=""
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {fieldConfig.showLastName && (    
              <div className="flex flex-col gap-2 md:col-span-4">
                <FormField
                  control={control}
                  name={"lastName" as never}
                  rules={{
                    ...ValidationRules.required("Last name"),
                    ...ValidationRules.minLength("Last name", 2)
                  }}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          value={(field.value as string) || ""}
                          onChange={(e) => { field.onChange(e); }}
                          maxLength={500}
                          placeholder=""
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {/* Email and Phone - Two Column Layout */}
        {(fieldConfig.showWorkEmail || fieldConfig.showPhone) && (
          <div className="grid md:grid-cols-2 gap-6 md:gap-4">
            {fieldConfig.showWorkEmail && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={"workEmail" as never}
                  rules={ValidationRules.email("Work email")}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Work Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          {...field}
                          value={(field.value as string) || ""}
                          onChange={(e) => { field.onChange(e); }}
                          maxLength={500}
                          placeholder=""
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {fieldConfig.showPhone && (
              <div className="flex flex-col gap-2">
                <FormField
                  control={control}
                  name={"phone" as never}
                  rules={ValidationRules.phone("Phone number")}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          {...field}
                          value={(field.value as string) || ""}
                          onChange={(e) => { field.onChange(e); }}
                          maxLength={100}
                          placeholder=""
                          className="w-full"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}
          </div>
        )}

        {/* How did you hear about us */}
        {fieldConfig.showHowDidYouHear && (
          <div className="flex flex-col gap-2">
            <FormLabel>How did you hear about us?</FormLabel>
            <FormField
              control={control}
              name={"howDidYouHear" as never}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Select
                      value={(field.value as string) || ""}
                      onValueChange={(value) => { field.onChange(value); }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {referralSources.map((source: string) => (
                          <SelectItem key={source} value={source}>{source}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Referred by - conditional field */}
        {shouldShowReferredBy && (
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name={"referredBy" as never}
              rules={{
                ...ValidationRules.required("Referred by"),
                // ...ValidationRules.minLength("Referred by", 2)
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Referred by</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={(field.value as string) || ""}
                      onChange={(e) => { field.onChange(e); }}
                      maxLength={100}
                      placeholder=""
                      className="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {/* Message Field */}
        {fieldConfig.showMessage && (
          <div className="flex flex-col gap-2">
            <FormField
              control={control}
              name={"message" as never}
              render={({ field }) => {
                const messageValue = (field.value as string) || "";
                const characterCount = messageValue.length;
                const maxLength = 4000;
                
                return (
                  <FormItem>
                    <FormLabel>Message <span className="font-normal text-muted-foreground">(Optional)</span></FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        value={messageValue}
                        onChange={(e) => { field.onChange(e); }}
                        placeholder=""
                        rows={6}
                        maxLength={maxLength}
                        // className="resize-none"
                      />
                    </FormControl>
                    <div className="flex justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        {formState.errors.message ? (
                          <FormMessage />
                        ) : (
                          <p className="paragraph-sm text-muted-foreground">
                            {fieldConfig.messageHelperText || "Add anything you'd like our team to know. Questions, business needs, timing, or how we can support you best."}
                          </p>
                        )}
                      </div>
                      <p className="paragraph-sm text-muted-foreground whitespace-nowrap flex-shrink-0">
                        {characterCount} / {maxLength}
                      </p>
                    </div>
                  </FormItem>
                );
              }}
            />
            
          </div>
        )}
        
        {/* Consent Checkboxes */}
        {(fieldConfig.showTermsAccepted || fieldConfig.showMarketingConsent || fieldConfig.showCreditReportConsent) && (
          <div className="flex flex-col gap-2">
            {fieldConfig.showTermsAccepted && (
              <FormField
                control={control}
                name={"termsAccepted" as never}
                rules={ValidationRules.mustBeTrue("terms and conditions")}
                render={({ field }) => (
                  <FormItem>
                    {/* <FormLabel>Terms and Conditions</FormLabel> */}
                    <FormControl>
                      <label htmlFor="termsAccepted" className="flex gap-3 cursor-pointer">
                        <Checkbox
                          id="termsAccepted"
                          checked={!!field.value}
                          onCheckedChange={(checked) => { field.onChange(checked); }}
                          className="cursor-pointer mt-[1px] xl:mt-[3px]"
                        />
                        <p className="paragraph-sm leading-relaxed font-normal">
                          I&apos;ve read and agree to the{' '}
                          <Link target="_blank" href="/legal/conditions-of-use">Conditions of Use</Link>
                          {' '}and{' '}
                          <Link target="_blank" href="/legal/privacy-policy">Privacy Policy</Link>.
                        </p>
                      </label>
                    </FormControl>
                    <FormMessage className="ml-7" />
                  </FormItem>
                )}
              />
            )}

            {fieldConfig.showCreditReportConsent && (
              <FormField
                control={control}
                name={"creditReportConsent" as never}
                rules={ValidationRules.mustBeTrue("credit report consent")}
                render={({ field }) => (
                  <FormItem>
                    {/* <FormLabel>Credit Report Consent</FormLabel> */}
                    <FormControl>
                      <label htmlFor="creditReportConsent" className="flex gap-3 cursor-pointer">
                        <Checkbox
                          id="creditReportConsent"
                          checked={!!field.value}
                          onCheckedChange={(checked) => { field.onChange(checked); }}
                          className="cursor-pointer mt-[1px] xl:mt-[3px]"
                        />
                        <p className="paragraph-sm leading-relaxed font-normal">
                          I authorise Octet to collect and use my information to assess my enquiry, including obtaining credit reports from credit reporting bodies.
                        </p>
                      </label>
                    </FormControl>
                    <FormMessage className="ml-7" />
                  </FormItem>
                )}
              />
            )}

            {fieldConfig.showMarketingConsent && (
              <FormField
                control={control}
                name={"marketingConsent" as never}
                render={({ field }) => (
                  <FormItem>
                    {/* <FormLabel>Marketing Consent</FormLabel> */}
                    <FormControl>
                      <label htmlFor="marketingConsent" className="flex gap-3 cursor-pointer">
                        <Checkbox
                          id="marketingConsent"
                          checked={!!field.value}
                          onCheckedChange={(checked) => { field.onChange(checked); }}
                          className="cursor-pointer mt-[1px] xl:mt-[3px]"
                        />
                        <p className="paragraph-sm leading-relaxed font-normal">
                          I&apos;d like to receive occasional news and updates from Octet by email. <span className="font-normal text-muted-foreground">(Optional)</span>
                        </p>
                      </label>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}
          </div>
        )}
        
      </div>

      {/* Button removed - now handled by centralized submit button */}
    </div>
  );
}; 